import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Content from "../../components/Content/Content";
import { ContentPageClient } from "../../clients/ContentPageClient";
import styles from "./ContentPage.module.css";
import {
  cleanUpContentPageSlides,
  type ContentPageSlideProps,
} from "../../utils/contentful-helpers";
import { IconButton } from "@mui/material";
import {
  handleButtonClickEventBuilder,
  pageStartEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { PreSurveyModal } from "../../components/PreSurveyModal/PreSurveyModal";
import { useWithLtikSession } from "../../hooks/use-with-ltik-session";
import { useAppDispatch, useAppSelector } from "../../app/store";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import useTenant from "../../tenancy/useTenantPath";

export const ContentPage = ({
  completedPreSurvey,
  isLtikSession,
}: {
  completedPreSurvey: boolean;
  isLtikSession: boolean;
}) => {
  const [contentData, setContentData] = useState<ContentPageSlideProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { canvasId } = useWithLtikSession();
  const { userId, sessionId } = useAppSelector((state) => state.session);
  const navigate = useNavigate();
  const tenant = useTenant();
  const dispatch = useAppDispatch();

  const sortContentByTags = useCallback((content: ContentPageSlideProps[]) => {
    const orderTags = ["1", "2", "3", "4"];
    return content.sort((a, b) => {
      const aIndex = orderTags.findIndex((tag) => a.tags.includes(tag));
      const bIndex = orderTags.findIndex((tag) => b.tags.includes(tag));
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await ContentPageClient.getEntries({
          content_type: "contentPage",
        });
        const cleanedData = cleanUpContentPageSlides(response.items);
        const sortedData = sortContentByTags(cleanedData);
        setContentData(sortedData);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching content:", err);
        setError("Failed to fetch content. Please try again later.");
        setIsLoading(false);
      }
    };

    void fetchContent();
  }, [sortContentByTags]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) =>
      prev < contentData.length - 1 ? prev + 1 : prev,
    );
  };

  const handleLetsGo = () => {
    if (!userId || !sessionId) return;

    const event = handleButtonClickEventBuilder(
      userId,
      sessionId,
      "Let's Go",
      {},
    );
    void dispatch(sendEvent(event));
    navigate(tenant.basePath + "/mentorpal");
  };

  useEffect(() => {
    if (!userId || !sessionId) return;

    const pageUrl = window.location.origin + window.location.pathname;
    const curSlide = contentData[currentSlide];
    if (!curSlide) return;
    const startPageEvent = pageStartEventBuilder(userId, sessionId, pageUrl, {
      slide: curSlide.title,
      slideIndex: currentSlide,
      totalSlides: contentData.length,
    });
    void dispatch(sendEvent(startPageEvent));
  }, [currentSlide, userId, sessionId, contentData, dispatch]);

  if (isLoading) return <div className={styles.loading}>Loading...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.contentPage}>
      <PreSurveyModal
        open={!completedPreSurvey && isLtikSession}
        canvasId={canvasId}
      />
      {contentData.length > 0 ? (
        <div className={styles.carousel}>
          {currentSlide > 0 && (
            <div className={styles.arrowLeft}>
              <IconButton
                onClick={handlePrevSlide}
                aria-label="Previous Slide"
                style={{ padding: "0" }}
              >
                <ArrowUpwardIcon
                  style={{
                    transform: "rotate(-90deg)",
                    fontSize: "4rem",
                    color: "#a9b9ff",
                  }}
                />
              </IconButton>
            </div>
          )}
          <div className={styles.slidesContainer}>
            {contentData.map((content, index) => (
              <div
                key={content.id}
                className={
                  index === currentSlide ? styles.slide : styles.hiddenSlide
                }
              >
                <Content
                  title={content.title}
                  displayName={content.displayName}
                  description={content.description}
                  images={content.images}
                />
              </div>
            ))}
          </div>
          {currentSlide < contentData.length - 1 ? (
            <div className={styles.arrowRight}>
              <IconButton
                onClick={handleNextSlide}
                aria-label="Next Slide"
                style={{ padding: "0" }}
                data-cy="next-slide-button"
              >
                <ArrowUpwardIcon
                  style={{
                    transform: "rotate(90deg)",
                    fontSize: "4rem",
                    color: "#a9b9ff",
                  }}
                />
              </IconButton>
            </div>
          ) : (
            <div className={styles.arrowRight}>
              <IconButton
                onClick={handleLetsGo}
                aria-label="Let's Go"
                style={{ padding: "0" }}
                data-cy="lets-go-button"
              >
                <KeyboardDoubleArrowUpIcon
                  style={{
                    transform: "rotate(90deg)",
                    fontSize: "4rem",
                    color: "#a9b9ff",
                  }}
                />
              </IconButton>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.arrowRight}>
          <IconButton
            onClick={handleLetsGo}
            aria-label="Let's Go"
            style={{ padding: "0" }}
            data-cy="lets-go-button"
          >
            <KeyboardDoubleArrowUpIcon
              style={{
                transform: "rotate(90deg)",
                fontSize: "4rem",
                color: "#a9b9ff",
              }}
            />
          </IconButton>
        </div>
      )}
    </div>
  );
};
